import { findQueryRanges, type MatchRange } from "../utils/searchHighlight";

interface HighlightedTextProps {
  /** The text to display */
  text: string;
  /** Search query to highlight */
  searchQuery?: string;
  /** Index of the currently active match within this field (-1 for none) */
  activeMatchIndex?: number;
  /**
   * Explicit ranges to mark, for fields whose match isn't a literal substring
   * of the rendered text — e.g. a date displayed as "03/05/2024" matched by the
   * query "2024-03-05". Overrides `searchQuery` matching when provided.
   */
  ranges?: MatchRange[];
}

/**
 * Renders text with search term highlighting using React elements.
 * Used for plain text fields (title, doctor name, date) where
 * dangerouslySetInnerHTML is not appropriate. For markdown content, use
 * highlightSearchTerm() instead.
 */
export const HighlightedText: React.FC<HighlightedTextProps> = ({
  text,
  searchQuery,
  activeMatchIndex = -1,
  ranges,
}) => {
  const matches = ranges ?? findQueryRanges(text, searchQuery);

  if (matches.length === 0) {
    return <>{text}</>;
  }

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  matches.forEach((range, matchIndex) => {
    if (range.start > cursor) {
      parts.push(<span key={cursor}>{text.slice(cursor, range.start)}</span>);
    }
    parts.push(
      <mark
        key={range.start}
        className={
          matchIndex === activeMatchIndex
            ? "indigo-timeline-item__mark--active"
            : undefined
        }
      >
        {text.slice(range.start, range.end)}
      </mark>,
    );
    cursor = range.end;
  });

  if (cursor < text.length) {
    parts.push(<span key={cursor}>{text.slice(cursor)}</span>);
  }

  return <>{parts}</>;
};
